import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, shareReplay } from 'rxjs';
import { CvOptionsResponse, CvOptions } from '../models/cv-options.model';
import { EnvironmentService } from '../../environments/environment-loader';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CvOptionsService {
  private apiUrl: string;
  private cachedOptions: Observable<CvOptionsResponse> | null = null;
  private serverSideOptions: CvOptionsResponse | null = null;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private environmentService: EnvironmentService
  ) {
    this.apiUrl = this.environmentService.environment.apiUrl;
  }

  /**
   * Fetches CV options data from the API
   * Caches the result to avoid multiple API calls
   * Uses server-side cache during SSR
   * @returns Observable with CV options data including languages, skills, etc.
   */
  getCvOptions(): Observable<CvOptionsResponse> {
    // If we have server-side options and we're on the server, return them
    if (isPlatformServer(this.platformId) && this.serverSideOptions) {
      return of(this.serverSideOptions);
    }
    
    // If we have cached options from a previous request, return them
    if (this.cachedOptions) {
      return this.cachedOptions;
    }
    
    // Otherwise fetch from API and cache the result
    this.cachedOptions = this.http.get<CvOptionsResponse>(`${this.apiUrl}/cv-options`)
      .pipe(
        tap(response => {
          // Store in server-side cache if we're on the server
          if (isPlatformServer(this.platformId)) {
            this.serverSideOptions = response;
          }
        }),
        catchError(error => {
          console.error('Error fetching CV options:', error);
          // Return empty arrays to avoid showing untranslatable hardcoded values
          return of({
            status: 200,
            message: 'Error fetching options',
            body: {
              genders: [],
              languages: [],
              languageLevels: [],
              skills: [],
              skillLevels: [],
              benefits: null,
              educationLevels: []
            }
          });
        }),
        shareReplay(1)
      );
    
    return this.cachedOptions;
  }
}
